import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';
import { useUserAuthOptional } from './UserAuthContext';
import {
  useUserPrefsSync,
  loadAnonStoredPrefs,
  saveAnonStoredPrefs,
  loadUserStoredPrefs,
  updateUserStoredPrefs,
  ANON_PROVENANCE,
  LOCAL_ONLY_PROVENANCE,
  LEGACY_PREFS_KEY,
  ANON_PREFS_KEY,
  AMBIGUOUS_MESSAGE,
  CONFLICT_MESSAGE,
  DEFAULT_RADIUS_KM,
  syncPersonProperties,
  type PrefsProvenance,
  type PrefsSyncNotice,
} from '../hooks/useUserPrefsSync';
import { isTerritoryPath, territoryRouteKey } from '../routes/territoryPaths';
import { usePrimaryRoutesLocation } from '../routes/usePrimaryRoutesLocation';
import { categoryShortLabels } from '../utils/categories';
import { useDiscoveryMode } from './discoveryStore';
import { placeByGps } from '../utils/gpsPlacement';
import {
  findRegistryById,
  loadRegistry,
  matchRegistryPair,
  registryCityToItem,
} from '../utils/registryLookup';
import {
  convertV1PrefsToV2,
  fingerprintV1Location,
  loadV2Stored,
  rewriteV1GpsLocations,
  saveV2Stored,
  updateV2Stored,
  MULTI_CITY_MESSAGE,
  V2_ANON_KEY,
  type V2PrefsPatch,
} from '../hooks/prefsV2Storage';
import { useUserPrefsSyncV2 } from '../hooks/useUserPrefsSyncV2';
import type { CityItem, LocationChoice, UserPreferencesV2 } from '@buzzmap/shared';

/**
 * Category options with labels and emojis. Labels come from the single
 * short-name map (`categoryShortLabels`): these are the navigation/filter
 * labels shown in tight UI (home chips, filter pills, favorite pickers), so
 * they take the short form. Ids must stay literal DB values — `CategoryId`
 * derives from them, so this array can't be generated with `.map()`.
 */
export const categoryOptions = [
  { id: 'mercatino', label: categoryShortLabels.mercatino, emoji: '' },
  { id: 'sagra', label: categoryShortLabels.sagra, emoji: '' },
  { id: 'fiera', label: categoryShortLabels.fiera, emoji: '' },
  { id: 'musica', label: categoryShortLabels.musica, emoji: '' },
  { id: 'bambini', label: categoryShortLabels.bambini, emoji: '' },
  { id: 'sport', label: categoryShortLabels.sport, emoji: '' },
  { id: 'cinema_teatro', label: categoryShortLabels.cinema_teatro, emoji: '' },
  { id: 'tradizione', label: categoryShortLabels.tradizione, emoji: '' },
  { id: 'festivita', label: categoryShortLabels.festivita, emoji: '' },
  { id: 'rievocazione', label: categoryShortLabels.rievocazione, emoji: '' },
  { id: 'arte', label: categoryShortLabels.arte, emoji: '' },
] as const;

export type CategoryId = (typeof categoryOptions)[number]['id'];

// CityItem and LocationChoice moved to @buzzmap/shared (T004-BE): the
// shared package needs them for TerritoryResolution, and the dependency
// can only run web -> shared, not the other way. Re-exported here so no
// existing import in the web app has to change.
export type { CityItem, LocationChoice };

/**
 * User preferences interface
 */
export interface UserPreferences {
  location: LocationChoice;
  favoriteCategories: string[];
}

/**
 * Default user preferences
 */
const defaultPreferences: UserPreferences = {
  location: { type: 'none' },
  favoriteCategories: [],
};

// --- T011-FE: user-facing copy for v2 placement outcomes -------------------
const GPS_PLACEMENT_MESSAGE = 'Posizione non disponibile: scegli il tuo comune per continuare.';
const CITY_NOT_FOUND_MESSAGE = 'Comune non trovato: riprova a sceglierlo dall’elenco.';
const CITY_LOOKUP_FAILED_MESSAGE =
  'Non è stato possibile verificare il comune: riprova, oppure sceglierlo manualmente.';
/**
 * W1 (T015 review): a deliberate pick/save while the v1→v2 conversion is still
 * in flight must not vanish silently — the record it would commit onto does not
 * exist yet, so the action is refused with a visible, retryable notice.
 */
const CONVERSION_PENDING_MESSAGE = 'Salvataggio in corso: riprova tra un istante.';

/** Saved-radius bounds: the same integer 1..200 contract as the preferences API. */
const MIN_SAVED_RADIUS_KM = 1;
const MAX_SAVED_RADIUS_KM = 200;

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((s) => typeof s === 'string');
}

function validateCityItem(v: unknown): CityItem | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (typeof o.name !== 'string') return null;
  if (!isFiniteNumber(o.lat) || !isFiniteNumber(o.lng)) return null;
  const item: CityItem = { name: o.name, lat: o.lat, lng: o.lng };
  if (typeof o.province === 'string') item.province = o.province;
  return item;
}

function validateLocationChoice(v: unknown): LocationChoice | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (o.type === 'none') return { type: 'none' };
  if (o.type === 'cities') {
    if (!Array.isArray(o.cities)) return null;
    if (!isFiniteNumber(o.radiusKm)) return null;
    const cities: CityItem[] = [];
    for (const raw of o.cities) {
      const c = validateCityItem(raw);
      if (!c) return null;
      cities.push(c);
    }
    return { type: 'cities', cities, radiusKm: o.radiusKm };
  }
  if (o.type === 'gps') {
    if (
      !Array.isArray(o.coords) ||
      o.coords.length !== 2 ||
      !isFiniteNumber(o.coords[0]) ||
      !isFiniteNumber(o.coords[1])
    )
      return null;
    if (!isFiniteNumber(o.radiusKm)) return null;
    if (!isFiniteNumber(o.ts)) return null;
    const result: LocationChoice = {
      type: 'gps',
      coords: [o.coords[0], o.coords[1]],
      radiusKm: o.radiusKm,
      ts: o.ts,
    };
    if (typeof o.derivedCityName === 'string') {
      result.derivedCityName = o.derivedCityName;
    }
    return result;
  }
  return null;
}

/**
 * Migrate already-parsed legacy preferences value to the new shape.
 * Caller must JSON.parse before invoking. Returns defaults on any invalid input.
 */
export function migrateLegacyPrefs(raw: unknown): {
  location: LocationChoice;
  favoriteCategories: string[];
} {
  const fallback = {
    location: { type: 'none' } as LocationChoice,
    favoriteCategories: [],
  };
  if (!raw || typeof raw !== 'object') return fallback;
  const o = raw as Record<string, unknown>;
  const favoriteCategories = isStringArray(o.favoriteCategories) ? o.favoriteCategories : [];
  // Legacy field `selectedCategory` (any type) is silently dropped here for backward compat.
  const validatedLocation = validateLocationChoice(o.location);
  if (validatedLocation) {
    return { location: validatedLocation, favoriteCategories };
  }
  if ('city' in o) {
    return { location: { type: 'none' }, favoriteCategories };
  }
  return fallback;
}

/**
 * T030 storage layers (see useUserPrefsSync.ts for the full contract):
 *  1. ANON_PREFS_KEY - the current anonymous view, with per-field provenance.
 *  2. LEGACY_PREFS_KEY - the pre-T030 owner-less store. Read for local
 *     continuity only; never attributed to an account (provenance
 *     'local-only'). This is also the key the reverted v1 lane keeps writing.
 *  3. userPrefsStorageKey(userId) - per-account local cache (written by the
 *     persistence effect below).
 */
function readLegacyPrefsFromStorage(): UserPreferences | null {
  if (typeof window === 'undefined') return null;
  const saved = window.localStorage.getItem(LEGACY_PREFS_KEY);
  if (!saved) return null;
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(saved);
  } catch {
    console.warn('Failed to parse user preferences from localStorage');
    return null;
  }
  return migrateLegacyPrefs(parsed);
}

function loadInitialPrefs(): { prefs: UserPreferences; provenance: PrefsProvenance } {
  const anon = loadAnonStoredPrefs();
  if (anon) return { prefs: anon.prefs, provenance: anon.provenance };
  const legacy = readLegacyPrefsFromStorage();
  if (legacy) return { prefs: legacy, provenance: LOCAL_ONLY_PROVENANCE };
  return { prefs: defaultPreferences, provenance: ANON_PROVENANCE };
}

/**
 * User context value interface
 */

export interface UserContextType {
  preferences: UserPreferences;
  /**
   * T011-FE: the AUTHORITATIVE saved intent while the discovery gate is open
   * (`v2`), `null` in legacy/loading (the v2 store is not hydrated there and
   * must not be — the v1 stores stay the authority until activation, plan
   * l.74). Shape is `UserPreferencesV2`: a single comune UUID plus an
   * INDEPENDENT `radiusKm`, so `none`/`needs_confirmation` keeps its radius
   * without activating geography (plan l.69).
   */
  preferencesV2: UserPreferencesV2 | null;
  /**
   * Classified outcome: the current identity is NULL and the user must confirm
   * a comune (migration/server `locationNeedsConfirmation`, plan l.79).
   * Radius and favorites are retained; no territory is activated.
   */
  locationNeedsConfirmation: boolean;
  setLocationChoice: (choice: LocationChoice) => void;
  clearLocation: () => void;
  /**
   * T015-FE: save exactly ONE comune, identified by its current registry UUID
   * (plan l.69 — `LocationChoice` v2 is `none` or one comune by UUID). The
   * choice REPLACES whatever was saved; there is no append path. `radiusKm`
   * defaults to the radius already saved. Resolves `true` only when the choice
   * was applied: an unknown id or an unreachable registry keeps the previous
   * identity and reports a notice instead of guessing a city (plan l.61/78).
   */
  setMunicipality: (municipalityId: string, radiusKm?: number) => Promise<boolean>;
  /**
   * T015-FE: persist the independent saved radius (`UserPreferences.radiusKm`,
   * plan l.69) — the profile's radius control, which has no URL to update.
   * The active radius of a territorial view stays the URL's business.
   */
  setSavedRadiusKm: (radiusKm: number) => void;
  toggleCategory: (category: string) => void;
  setFavoriteCategories: (categories: string[]) => void;
  resetPreferences: () => void;
  /**
   * @deprecated LEGACY-MODE MACHINERY (T010-FE). The session-only location
   * override that drives the pre-activation (`legacy`) behaviour. In `v2`
   * discovery mode the normalized URL is the sole authority and
   * `DiscoveryProvider` owns the active location — consumers should read
   * `effectiveLocation` (which `useUserPreferences` bridges from the resolved
   * territory in v2), never this getter directly. Kept as a read-only
   * projection for old callers during implementation; T011–T016 migrate the
   * consumers and retire it (the "final v2 client" then shows an explicit
   * upgrade state rather than falling back to this path).
   */
  locationOverride: LocationChoice | null;
  /** @deprecated Internal to the legacy resolution path (`useTerritoryRouteResolution`). */
  setLocationOverride: (loc: LocationChoice) => void;
  /** @deprecated Internal to the legacy resolution path. */
  clearLocationOverride: () => void;
  /**
   * The active location. In `legacy` mode: `locationOverride` if set, else the
   * saved preference. In `v2` mode (bridged by `useUserPreferences`): the
   * resolved territory's location (URL authority) if present, else the saved
   * preference. What consumers should read.
   */
  effectiveLocation: LocationChoice;
  /**
   * True while the active location is not yet known and the feed must hold its
   * fetch (no nationwide interim query). In `legacy` mode: from first render
   * whenever the current path is a territory route whose `/api/territory`
   * resolution hasn't landed yet. In `v2` mode (bridged): while a territory
   * resolution or a central generic-entry replacement is in flight.
   */
  locationResolutionPending: boolean;
  /** Clears the legacy pending gate (no-op in v2, where `DiscoveryProvider` owns it). */
  clearLocationResolutionPending: () => void;
  /**
   * User-visible notice for a preference outcome (409 conflict / 422
   * ambiguity / migration and GPS-placement outcomes of the T011-FE
   * conversion). The reverted v1 lane owns no revision, so it has nothing to
   * report: in `legacy` this is always null and only the v2 protocol fills it.
   */
  prefsSyncNotice: PrefsSyncNotice | null;
  dismissPrefsSyncNotice: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

/**
 * User preferences provider component.
 * Persists preferences to localStorage.
 */
export function UserProvider({ children }: UserProviderProps) {
  // Territory-resolution pending flag. Must be known SYNCHRONOUSLY on first
  // render — a useEffect would run after EventsContext's own fetch effect
  // has already fired the nationwide query T014-FE was throwing away.
  //
  // The pathname read here is the PRIMARY ROUTES location (the same one
  // `AppRoutes` renders its `<Routes>` against, via `usePrimaryRoutesLocation`)
  // — NOT the raw browser location. This component sits under `BrowserRouter`
  // but outside `<Routes>`, so `useLocation()` here would flip between the
  // territory path and `/event/:id` when a desktop modal opens and closes,
  // re-arming the gate on close while the mounted HomePage (whose own
  // `useLocation` never changed) had no reason to clear it again — leaving
  // `useTopRankedEvents`/`useWeekendNearbyEvents` skipped forever. Compared
  // against the previous render's pathname; when it changed,
  // `setLocationResolutionPending` is called during render — a supported
  // React pattern for "adjusting state when a prop changes" that takes effect
  // before children render/fetch, unlike a `useEffect`.
  const { pathname, search } = usePrimaryRoutesLocation();
  const routeKey = territoryRouteKey(pathname, search);
  const [locationResolutionPending, setLocationResolutionPending] = useState<boolean>(() =>
    isTerritoryPath(pathname)
  );
  const previousRouteKeyRef = useRef(routeKey);
  if (routeKey !== previousRouteKeyRef.current) {
    previousRouteKeyRef.current = routeKey;
    const pending = isTerritoryPath(pathname);
    if (pending !== locationResolutionPending) {
      setLocationResolutionPending(pending);
    }
  }
  const clearLocationResolutionPending = useCallback(() => {
    setLocationResolutionPending(false);
  }, []);

  const [preferences, setPreferences] = useState<UserPreferences>(() => {
    // Synchronous hydration so child contexts (EventsContext) see real preferences on first render
    if (typeof window === 'undefined') return defaultPreferences;
    return loadInitialPrefs().prefs;
  });

  // One-time effect: detect legacy schema and log once. Persistence happens via the effect below.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = window.localStorage.getItem(LEGACY_PREFS_KEY);
    if (!saved) return;
    let parsed: unknown = null;
    try {
      parsed = JSON.parse(saved);
    } catch {
      return;
    }
    const isLegacy =
      !!parsed && typeof parsed === 'object' && 'city' in (parsed as Record<string, unknown>);
    if (isLegacy) {
      console.info('Migrated legacy user prefs');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-field provenance of the current view. 'anon' = value the account may
  // own (user-touched or freshly produced anonymously; persistable to the
  // per-user key and importable at login from the anonymous store).
  // 'local-only' = owner-less legacy residue: never attributed to an account
  // and never written into a per-user key.
  const provenanceRef = useRef<PrefsProvenance>(ANON_PROVENANCE);

  // Latest intended categories, chained synchronously across actions so two
  // toggles in the same batch don't lose the second one (React runs state
  // updaters lazily when another update is already queued).
  const categoriesRef = useRef<string[]>(preferences.favoriteCategories);
  categoriesRef.current = preferences.favoriteCategories;

  // T015-INT: end-user auth context (may be undefined if provider missing in tests).
  const { user } = useUserAuthOptional();
  const userId = user?.id ?? null;
  const userIdRef = useRef<string | null>(userId);
  userIdRef.current = userId;

  // Owner whose store has been hydrated. undefined = not yet: persistence is
  // held until then so the first write never lands on the wrong owner's key.
  const hydratedOwnerRef = useRef<string | null | undefined>(undefined);

  // Hydrate the active owner's storage on login/logout. A logged-in user gets
  // their own store when present (account isolation); an anonymous view comes
  // from the anonymous store, falling back to the owner-less legacy store.
  useEffect(() => {
    const owner = userIdRef.current;
    if (owner) {
      const stored = loadUserStoredPrefs(owner);
      if (stored) {
        // The stored values are this account's own cache: persistable.
        provenanceRef.current = ANON_PROVENANCE;
        setPreferences(stored.prefs);
      }
      // else: keep the current view until the server sync lands; it will not
      // be written to this owner's key until the user changes something or
      // the sync applies (see the persistence effect guard below).
    } else {
      const initial = loadInitialPrefs();
      provenanceRef.current = initial.provenance;
      setPreferences(initial.prefs);
    }
    hydratedOwnerRef.current = owner;
  }, [userId]);

  // --- T011-FE: v2 store (SEPARATE namespace) -----------------------------
  //
  // The discovery gate is published by `DiscoveryProvider`, which MOUNTS BELOW
  // this provider (it reads the saved intent from here), so `mode` arrives
  // through the leaf signal instead of context. Until the gate actually opens
  // every v1 path above and below stays byte-identical (plan l.69-74).
  const discoveryMode = useDiscoveryMode();
  const v2Active = discoveryMode === 'v2';

  const [prefsV2, setPrefsV2State] = useState<UserPreferencesV2 | null>(null);
  const [locationNeedsConfirmation, setLocationNeedsConfirmation] = useState(false);
  const [migrationNotice, setMigrationNotice] = useState<PrefsSyncNotice | null>(null);
  /** false while a conversion fault left the record unwritten (retry next session). */
  const [v2StoreReady, setV2StoreReady] = useState(false);
  /** Registry projection of the saved comune, keyed by id (read-only view, plan l.70). */
  const [projected, setProjected] = useState<{ id: string; city: CityItem } | null>(null);
  const v2ProvenanceRef = useRef<PrefsProvenance>(ANON_PROVENANCE);
  /** Owner whose record the in-memory `prefsV2` was hydrated from. */
  const v2OwnerRef = useRef<string | null | undefined>(undefined);
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;
  /** Bumped by every manual location choice: a late GPS placement never applies. */
  const manualChoiceEpochRef = useRef(0);
  const gpsRunRef = useRef(0);
  const conversionRunRef = useRef(0);

  /** The v1 view that belongs to `owner` — mirrors the v1 hydration rules exactly. */
  const readV1ViewForOwner = (
    owner: string | null
  ): { prefs: UserPreferences; provenance: PrefsProvenance } => {
    if (owner) {
      const stored = loadUserStoredPrefs(owner);
      if (stored) return { prefs: stored.prefs, provenance: ANON_PROVENANCE };
      return { prefs: preferencesRef.current, provenance: provenanceRef.current };
    }
    const initial = loadInitialPrefs();
    return { prefs: initial.prefs, provenance: initial.provenance };
  };

  /**
   * The v1 outbox converted for v2: radius/favorites are recoverable
   * NON-location intents and survive (plan l.74); a queued `city`/`province`
   * is a stale location intent and is dropped — the migration decides the
   * identity, a queued patch must never replay old geography (plan l.78,
   * T008's "old location-only outbox intent is held").
   */
  const convertedV1Outbox = (owner: string | null): V2PrefsPatch | null => {
    if (!owner) return null;
    const v1Pending = loadUserStoredPrefs(owner)?.pending;
    if (!v1Pending) return null;
    const patch: V2PrefsPatch = {};
    if ('radiusKm' in v1Pending) patch.radiusKm = v1Pending.radiusKm ?? null;
    if ('favoriteCategories' in v1Pending && v1Pending.favoriteCategories) {
      patch.favoriteCategories = [...v1Pending.favoriteCategories];
    }
    return Object.keys(patch).length > 0 ? patch : null;
  };

  // Persist to the active owner's store whenever preferences change.
  // Anonymous → ANON_PREFS_KEY (with provenance); logged-in → per-user key.
  // The legacy owner-less key is never written again ("resta locale"), and
  // legacy residue ('local-only') is never written into a per-user key.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const owner = userIdRef.current;
    if (hydratedOwnerRef.current === undefined || hydratedOwnerRef.current !== owner) return;
    if (owner) {
      const prov = provenanceRef.current;
      updateUserStoredPrefs(owner, (prev) => ({
        ...prev,
        prefs: {
          location: prov.location === 'local-only' ? prev.prefs.location : preferences.location,
          favoriteCategories:
            prov.favoriteCategories === 'local-only'
              ? prev.prefs.favoriteCategories
              : preferences.favoriteCategories,
        },
      }));
    } else {
      saveAnonStoredPrefs({ v: 2, prefs: preferences, provenance: provenanceRef.current });
    }
  }, [preferences]);

  // Exactly ONE protocol speaks at a time (T011-FE): the v1 hook below is
  // disabled while the gate is open, and `useUserPrefsSyncV2` (UUID wire
  // contract, T008) is disabled before it, so a mode flip can never replay a
  // v1 write. The reverted v1 lane owns no server API of its own — its writes
  // go through `persistUserPrefs` (direct Supabase) — so it has no notice
  // either; `prefsSyncNotice` is v2-only now.
  // Identity-stable updater: `useUserPrefsSyncV2` keys its whole sync chain on
  // it, so an inline arrow here would re-run a GET on every render.
  const setPrefsV2 = useCallback((updater: (prev: UserPreferencesV2) => UserPreferencesV2) => {
    setPrefsV2State((prev) => (prev ? updater(prev) : prev));
  }, []);
  const prefsSyncV2 = useUserPrefsSyncV2({
    prefs: prefsV2,
    setPrefs: setPrefsV2,
    needsConfirmation: locationNeedsConfirmation,
    setLocationNeedsConfirmation,
    enabled: v2Active,
  });
  const sendPatchV2 = prefsSyncV2.sendPatch;
  const notice = v2Active ? (migrationNotice ?? prefsSyncV2.notice) : null;
  const dismissNotice = useCallback(() => {
    setMigrationNotice(null);
    prefsSyncV2.dismissNotice();
  }, [prefsSyncV2]);

  // An explicit action makes that field the user's own value: importable from
  // the anonymous store at login, and persistable to the per-user key.
  const markProvenance = useCallback((field: keyof PrefsProvenance) => {
    provenanceRef.current = { ...provenanceRef.current, [field]: 'anon' };
  }, []);

  // T015-INT: localStorage <-> DB sync on auth state change (idempotent per user.id).
  // Uses a setter wrapper that updates internal state from merged DB+local values.
  const setPrefsForSync = useCallback((next: UserPreferences) => {
    setPreferences(next);
  }, []);
  useUserPrefsSync({ preferences, setPreferences: setPrefsForSync, enabled: !v2Active });

  // Helper: persist a partial update to the `users` row when authenticated.
  // T017: after a successful UPDATE, push the changed fields to PostHog person properties.
  const persistUserPrefs = useCallback(
    (update: {
      city?: string | null;
      radius_km?: number | null;
      favorite_categories?: string[];
    }) => {
      if (!user) return;
      const userId = user.id;
      void (async () => {
        try {
          const { error } = await supabase.from('users').update(update).eq('id', userId);
          if (error) {
            console.warn('[UserContext] persist prefs failed:', error.message);
            return;
          }
          // T017: sync ONLY the fields explicitly updated; leave others untouched.
          const phProps: Parameters<typeof syncPersonProperties>[0] = {};
          if ('city' in update) phProps.preferred_city = update.city ?? null;
          if ('radius_km' in update) phProps.preferred_radius_km = update.radius_km ?? null;
          if ('favorite_categories' in update) {
            phProps.preferred_categories =
              update.favorite_categories && update.favorite_categories.length > 0
                ? update.favorite_categories
                : null;
          }
          syncPersonProperties(phProps);
        } catch (err) {
          console.warn('[UserContext] persist prefs threw:', err);
        }
      })();
    },
    [user]
  );

  // Session-only location override (e.g. from a territorial URL). Never persisted, never synced.
  const [locationOverride, setLocationOverrideState] = useState<LocationChoice | null>(null);

  const setLocationOverride = useCallback((loc: LocationChoice) => {
    setLocationOverrideState(loc);
  }, []);

  const clearLocationOverride = useCallback(() => {
    setLocationOverrideState(null);
  }, []);

  // --- T011-FE: v2 hydration / one-time conversion -------------------------

  useEffect(() => {
    const runId = ++conversionRunRef.current;
    if (!v2Active) {
      // Leaving v2 (a caps refetch dropping back to legacy): the v1 stores are
      // untouched and become the authority again, exactly as before activation.
      if (prefsV2 !== null) {
        setPrefsV2State(null);
        setV2StoreReady(false);
        setLocationNeedsConfirmation(false);
        setProjected(null);
        v2OwnerRef.current = undefined;
      }
      return;
    }
    // The URL is the authority in v2: any session override left by the legacy
    // resolution path must not survive the gate opening.
    setLocationOverrideState(null);
    // W4 (T011 review): a notice left by the PREVIOUS owner (migration /
    // needs-confirmation) must never survive an account switch — clear it
    // before this owner's outcome is classified.
    setMigrationNotice(null);

    const owner = userId;
    // W6 (T011 review): only the owner's OWN stored v1 record — or, for the
    // anonymous key, the anon/legacy view it already owns — may be converted
    // and durably written. An owner with no stored record keeps whatever the
    // v1 lane shows in memory (the previous account's view, or owner-less
    // residue that v1 never attributes to an account), so their conversion
    // starts from DEFAULTS; an anonymous choice still reaches the account later
    // through the provenance-gated v2 anon import.
    const attributable = owner === null || loadUserStoredPrefs(owner) !== null;
    const existing = loadV2Stored(owner);
    if (existing) {
      // Initialized v2 record WINS: a v1 store (this tab's or an old tab's) is
      // never re-imported over it — drift is only REPORTED as a recoverable
      // conflict (plan l.74).
      v2OwnerRef.current = owner;
      setPrefsV2State(existing.prefs);
      v2ProvenanceRef.current = existing.provenance;
      setLocationNeedsConfirmation(existing.locationNeedsConfirmation === true);
      setV2StoreReady(true);
      // H1: after a DURABLE conversion the v1 entry may have been REWRITTEN
      // (raw GPS stripped), so the expected fingerprint is the post-strip one.
      // A later write from an old tab still differs from it and is reported.
      const expectedV1 = existing.strippedFrom ?? existing.convertedFrom;
      if (attributable && expectedV1) {
        const v1View = readV1ViewForOwner(owner);
        if (fingerprintV1Location(v1View.prefs.location) !== expectedV1) {
          setMigrationNotice({ kind: 'conflict', message: CONFLICT_MESSAGE });
        }
      }
      return;
    }

    // One-time conversion of the v1 view for THIS owner (plan l.78).
    const v1View = attributable
      ? readV1ViewForOwner(owner)
      : { prefs: defaultPreferences, provenance: ANON_PROVENANCE };
    setProjected(null);
    void convertV1PrefsToV2({
      location: v1View.prefs.location,
      favoriteCategories: v1View.prefs.favoriteCategories,
      provenance: v1View.provenance,
    }).then((conv) => {
      if (runId !== conversionRunRef.current) return;
      v2ProvenanceRef.current = conv.provenance;
      v2OwnerRef.current = owner;
      setPrefsV2State(conv.prefs);
      setLocationNeedsConfirmation(conv.needsConfirmation);
      if (conv.notice) setMigrationNotice(conv.notice);
      if (conv.durable) {
        // H1 (security sweep): a durable conversion must not leave the raw GPS
        // record behind in the v1 stores — the fix is rewritten to the resolved
        // comune's public registry center (or `none`), in every v1 key at once.
        // The in-memory v1 view follows the rewrite: the persistence effect
        // above (and any later drop back to legacy) must never resurrect the
        // coordinates the conversion just removed.
        if (conv.v1Replacement) {
          const stripped = conv.v1Replacement;
          rewriteV1GpsLocations(stripped, owner);
          setPreferences((prev) => ({ ...prev, location: stripped }));
        }
        saveV2Stored(owner, {
          v: 1,
          prefs: conv.prefs,
          provenance: conv.provenance,
          pending: convertedV1Outbox(owner),
          locationNeedsConfirmation: conv.needsConfirmation,
          convertedFrom: conv.convertedFrom,
          strippedFrom: conv.strippedFrom,
          migratedAt: Date.now(),
        });
        setV2StoreReady(true);
      } else {
        // Transient fault: raw coordinates are never persisted and the v1
        // store stays the durable source — retried on the next session.
        setV2StoreReady(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v2Active, userId]);

  // Persist the v2 record (separate namespace). Never writes the v1 stores.
  useEffect(() => {
    if (!v2Active || !prefsV2 || !v2StoreReady) return;
    const owner = userId;
    if (hydratedOwnerRef.current === undefined || hydratedOwnerRef.current !== owner) return;
    // The in-memory record still belongs to the PREVIOUS account (the render
    // right after a switch, before the conversion effect re-hydrates): never
    // write one user's geography into another user's key.
    if (v2OwnerRef.current !== owner) return;
    updateV2Stored(owner, (prev) => ({
      ...prev,
      prefs: prefsV2,
      provenance: v2ProvenanceRef.current,
      locationNeedsConfirmation,
    }));
  }, [prefsV2, locationNeedsConfirmation, v2Active, v2StoreReady, userId]);

  // Registry projection of the saved comune: labels/coordinates are REGISTRY
  // data (plan l.69), never stored in the preference record. Read-only — it
  // only feeds the v1-shape projection old callers still render.
  const savedComuneId =
    v2Active && prefsV2?.location.type === 'comune' ? prefsV2.location.municipalityId : null;
  useEffect(() => {
    if (!savedComuneId) {
      setProjected(null);
      return;
    }
    if (projected?.id === savedComuneId) return;
    let cancelled = false;
    findRegistryById(savedComuneId)
      .then((row) => {
        if (cancelled) return;
        setProjected(row ? { id: savedComuneId, city: registryCityToItem(row) } : null);
      })
      .catch(() => {
        if (cancelled) return;
        setProjected(null);
      });
    return () => {
      cancelled = true;
    };
  }, [savedComuneId, projected]);

  // --- T011-FE: v2 actions -------------------------------------------------

  /** Every manual choice bumps the epoch: a late GPS placement never applies. */
  const invalidateGpsPlacement = useCallback(() => {
    manualChoiceEpochRef.current += 1;
    gpsRunRef.current += 1;
  }, []);

  /** Apply one v2 change: state + optional outbox intent + notice/flag. */
  const commitV2 = useCallback(
    (
      updater: (prev: UserPreferencesV2) => UserPreferencesV2,
      patch: V2PrefsPatch | null,
      extra?: { notice?: PrefsSyncNotice | null; needsConfirmation?: boolean }
    ) => {
      setV2StoreReady(true);
      setPrefsV2State((prev) => (prev ? updater(prev) : prev));
      if (extra?.notice !== undefined) setMigrationNotice(extra.notice);
      if (extra?.needsConfirmation !== undefined) {
        setLocationNeedsConfirmation(extra.needsConfirmation);
      }
      if (patch) sendPatchV2(patch);
    },
    [sendPatchV2]
  );

  /**
   * v2 location choice. The legacy `LocationChoice` is still what old callers
   * (LocalitaPopup, T015) emit, so it is ADAPTED here and never stored:
   * - `gps` → the one placement path (acquire → contain → one high-accuracy
   *   retry → manual choice); raw coordinates are dropped on arrival;
   * - `cities` → its FIRST comune resolved through the registry (a many-center
   *   region must never become a multi-city preference, plan l.69), with a
   *   notice when the list had more;
   * - a choice that cannot be placed is never guessed (plan l.61/78).
   */
  const setLocationChoiceV2 = useCallback(
    (choice: LocationChoice) => {
      if (!prefsV2) return; // conversion in flight (sub-second): nothing to commit onto
      /** Apply a comune: identity + radius + the matching outbox intent. */
      const commitComune = (
        municipalityId: string,
        radiusKm: number,
        notice: PrefsSyncNotice | null
      ) => {
        commitV2(
          (prev) => ({ ...prev, radiusKm, location: { type: 'comune', municipalityId } }),
          { municipalityId, radiusKm },
          { notice, needsConfirmation: false }
        );
      };
      /** Keep the previous identity: only radius + a notice (never guess). */
      const commitRadiusOnly = (radiusKm: number, notice: PrefsSyncNotice) => {
        commitV2((prev) => ({ ...prev, radiusKm }), { radiusKm }, { notice });
      };
      if (choice.type === 'gps') {
        const epoch = manualChoiceEpochRef.current;
        const run = gpsRunRef.current + 1;
        gpsRunRef.current = run;
        const radiusKm = Number.isFinite(choice.radiusKm) ? choice.radiusKm : prefsV2.radiusKm;
        void placeByGps().then((outcome) => {
          // Manual Torino choice while the Crema request is pending wins, permanently.
          if (gpsRunRef.current !== run || manualChoiceEpochRef.current !== epoch) return;
          if (outcome.kind === 'resolved') {
            commitComune(outcome.municipality.id, radiusKm, null);
            return;
          }
          if (outcome.kind === 'ambiguous') {
            commitRadiusOnly(radiusKm, {
              kind: 'ambiguous-city',
              message: AMBIGUOUS_MESSAGE,
              candidates: outcome.candidates.map((c) => ({ city: c.name, province: c.province })),
            });
            return;
          }
          // unavailable / browser failure → manual choice; the previous
          // identity is KEPT (a failed fix never destroys a saved comune).
          commitRadiusOnly(radiusKm, {
            kind: 'location-unavailable',
            message: GPS_PLACEMENT_MESSAGE,
          });
        });
        return;
      }

      invalidateGpsPlacement();
      const radiusKm =
        choice.type === 'cities' && Number.isFinite(choice.radiusKm)
          ? choice.radiusKm
          : prefsV2.radiusKm;
      const first = choice.type === 'cities' ? choice.cities[0] : undefined;
      if (choice.type === 'none' || !first) {
        commitV2(
          (prev) => ({ ...prev, location: { type: 'none' } }),
          { municipalityId: null },
          {
            notice: null,
            needsConfirmation: false,
          }
        );
        return;
      }

      const epoch = manualChoiceEpochRef.current;
      void loadRegistry()
        .then((registry) => {
          if (manualChoiceEpochRef.current !== epoch) return; // superseded
          const matches = matchRegistryPair(registry.cities ?? [], {
            city: first.name,
            province: first.province,
          });
          const hit = matches.length === 1 && matches[0]?.path ? matches[0] : undefined;
          if (hit) {
            commitComune(
              hit.id,
              radiusKm,
              choice.type === 'cities' && choice.cities.length > 1
                ? { kind: 'migration', message: MULTI_CITY_MESSAGE }
                : null
            );
            return;
          }
          // Unresolvable pick: keep the previous identity, ask, never guess.
          commitRadiusOnly(
            radiusKm,
            matches.length > 1
              ? {
                  kind: 'ambiguous-city',
                  message: AMBIGUOUS_MESSAGE,
                  candidates: matches.map((c) => ({ city: c.name, province: c.province })),
                }
              : { kind: 'rejected', message: CITY_NOT_FOUND_MESSAGE }
          );
        })
        .catch(() => {
          if (manualChoiceEpochRef.current !== epoch) return;
          commitRadiusOnly(radiusKm, {
            kind: 'location-unavailable',
            message: CITY_LOOKUP_FAILED_MESSAGE,
          });
        });
    },
    [prefsV2, commitV2, invalidateGpsPlacement]
  );

  const setLocationChoice = useCallback(
    (choice: LocationChoice) => {
      if (v2Active) {
        setLocationChoiceV2(choice);
        return;
      }
      setPreferences((prev) => ({ ...prev, location: choice }));
      // An explicit choice must win over any territorial override in the same update,
      // otherwise effectiveLocation would keep returning the override for one frame.
      setLocationOverrideState(null);
      markProvenance('location');
      // Sync to DB only for cities-type choices (privacy: GPS coords never persisted).
      if (user) {
        if (choice.type === 'cities' && choice.cities.length > 0) {
          persistUserPrefs({ city: choice.cities[0]!.name, radius_km: choice.radiusKm });
        } else if (choice.type === 'gps' && choice.derivedCityName) {
          persistUserPrefs({ city: choice.derivedCityName, radius_km: choice.radiusKm });
        } else if (choice.type === 'none') {
          persistUserPrefs({ city: null });
        }
      }
    },
    [user, persistUserPrefs, markProvenance, v2Active, setLocationChoiceV2]
  );

  const clearLocation = useCallback(() => {
    if (v2Active) {
      invalidateGpsPlacement();
      // Explicit clear: the comune goes, the independent radius KEEPS its value
      // (plan l.69) — only `resetPreferences` restores the radius default.
      commitV2(
        (prev) => ({ ...prev, location: { type: 'none' } }),
        { municipalityId: null },
        {
          notice: null,
          needsConfirmation: false,
        }
      );
      return;
    }
    setPreferences((prev) => ({ ...prev, location: { type: 'none' } }));
    markProvenance('location');
    if (user) persistUserPrefs({ city: null });
  }, [user, persistUserPrefs, markProvenance, v2Active, commitV2, invalidateGpsPlacement]);

  /**
   * T015-FE: the saved location becomes exactly this comune (replace, never
   * append). The UUID is the authority — no name matching, no nearest guess:
   * an id the registry does not know, or a registry that cannot be read, keeps
   * the previous identity and only reports why (plan l.61/78, T015 acceptance).
   */
  const setMunicipality = useCallback(
    async (municipalityId: string, radiusKm?: number): Promise<boolean> => {
      if (v2Active) {
        if (!prefsV2) {
          // Conversion in flight: nothing to commit onto — say so instead of
          // swallowing the pick (W1).
          setMigrationNotice({ kind: 'location-unavailable', message: CONVERSION_PENDING_MESSAGE });
          return false;
        }
        invalidateGpsPlacement();
        const radius = isFiniteNumber(radiusKm) ? radiusKm : prefsV2.radiusKm;
        let row;
        try {
          row = await findRegistryById(municipalityId);
        } catch {
          setMigrationNotice({
            kind: 'location-unavailable',
            message: CITY_LOOKUP_FAILED_MESSAGE,
          });
          return false;
        }
        if (!row) {
          setMigrationNotice({ kind: 'rejected', message: CITY_NOT_FOUND_MESSAGE });
          return false;
        }
        commitV2(
          (prev) => ({ ...prev, radiusKm: radius, location: { type: 'comune', municipalityId } }),
          { municipalityId, radiusKm: radius },
          { notice: null, needsConfirmation: false }
        );
        return true;
      }
      // Legacy lane: same v1 commit the multi-city popup makes, one city only.
      let row;
      try {
        row = await findRegistryById(municipalityId);
      } catch {
        return false;
      }
      if (!row) return false;
      invalidateGpsPlacement();
      const fallback =
        preferences.location.type === 'none' ? DEFAULT_RADIUS_KM : preferences.location.radiusKm;
      setLocationChoice({
        type: 'cities',
        cities: [registryCityToItem(row)],
        radiusKm: isFiniteNumber(radiusKm) ? radiusKm : fallback,
      });
      return true;
    },
    [v2Active, prefsV2, commitV2, invalidateGpsPlacement, preferences.location, setLocationChoice]
  );

  /**
   * T015-FE: the independent saved radius (plan l.69). Clamped to the API's
   * integer 1..200 contract so a profile edit can never produce a 400.
   */
  const setSavedRadiusKm = useCallback(
    (radiusKm: number) => {
      if (!isFiniteNumber(radiusKm)) return;
      const km = Math.min(MAX_SAVED_RADIUS_KM, Math.max(MIN_SAVED_RADIUS_KM, Math.round(radiusKm)));
      if (v2Active) {
        if (!prefsV2) {
          // Same conversion window as setMunicipality (W1): a radius edit
          // cannot be committed onto a record that does not exist yet.
          setMigrationNotice({
            kind: 'location-unavailable',
            message: CONVERSION_PENDING_MESSAGE,
          });
          return;
        }
        if (prefsV2.radiusKm === km) return;
        commitV2((prev) => ({ ...prev, radiusKm: km }), { radiusKm: km });
        return;
      }
      // Keep the local v1 view in step so labels follow without a round-trip.
      // `none` has no radius slot in the v1 shape — the server value alone is
      // what today's profile write produced too.
      setPreferences((prev) =>
        prev.location.type === 'none'
          ? prev
          : { ...prev, location: { ...prev.location, radiusKm: km } }
      );
      if (preferences.location.type !== 'none') markProvenance('location');
      if (user) persistUserPrefs({ radius_km: km });
    },
    [user, persistUserPrefs, v2Active, prefsV2, commitV2, preferences.location, markProvenance]
  );

  const toggleCategory = useCallback(
    (category: string) => {
      if (v2Active) {
        if (!prefsV2) return;
        const next = prefsV2.favoriteCategories.includes(category)
          ? prefsV2.favoriteCategories.filter((c) => c !== category)
          : [...prefsV2.favoriteCategories, category];
        commitV2((prev) => ({ ...prev, favoriteCategories: next }), { favoriteCategories: next });
        return;
      }
      // Compute the next list inside the functional updater so rapid toggles
      // never read a stale closure (state side).
      setPreferences((prev) => {
        const next = prev.favoriteCategories.includes(category)
          ? prev.favoriteCategories.filter((c) => c !== category)
          : [...prev.favoriteCategories, category];
        return { ...prev, favoriteCategories: next };
      });
      // Sync side: chain through the latest intended list so two toggles in
      // the same batch don't lose the second one (React runs updaters lazily
      // when another update is already queued).
      const next = categoriesRef.current.includes(category)
        ? categoriesRef.current.filter((c) => c !== category)
        : [...categoriesRef.current, category];
      categoriesRef.current = next;
      markProvenance('favoriteCategories');
      if (user) persistUserPrefs({ favorite_categories: next });
    },
    [user, persistUserPrefs, markProvenance, v2Active, prefsV2, commitV2]
  );

  const setFavoriteCategories = useCallback(
    (categories: string[]) => {
      if (v2Active) {
        commitV2((prev) => ({ ...prev, favoriteCategories: categories }), {
          favoriteCategories: categories,
        });
        return;
      }
      setPreferences((prev) => ({ ...prev, favoriteCategories: categories }));
      categoriesRef.current = categories;
      markProvenance('favoriteCategories');
      if (user) persistUserPrefs({ favorite_categories: categories });
    },
    [user, persistUserPrefs, markProvenance, v2Active, commitV2]
  );

  const resetPreferences = useCallback(() => {
    if (v2Active) {
      invalidateGpsPlacement();
      setV2StoreReady(true);
      setPrefsV2State((prev) =>
        prev
          ? { location: { type: 'none' }, radiusKm: DEFAULT_RADIUS_KM, favoriteCategories: [] }
          : prev
      );
      setMigrationNotice(null);
      setLocationNeedsConfirmation(false);
      categoriesRef.current = [];
      // Explicit reset: every local residue is removed in BOTH namespaces so a
      // pre-activation view cannot resurface after the reset.
      try {
        window.localStorage.removeItem(LEGACY_PREFS_KEY);
        window.localStorage.removeItem(ANON_PREFS_KEY);
        window.localStorage.removeItem(V2_ANON_KEY);
      } catch {
        /* ignore */
      }
      prefsSyncV2.resetPreferences();
      return;
    }
    setPreferences(defaultPreferences);
    categoriesRef.current = [];
    // Explicit reset: the user's anonymous choices start fresh (importable
    // again) and every local residue is removed so it cannot resurface. This
    // is the reverted (pre-T030) reset: it clears LOCAL state only — the
    // account row is re-read and re-merged by `useUserPrefsSync` at the next
    // login, exactly as it was before the revisioned protocol existed.
    provenanceRef.current = ANON_PROVENANCE;
    try {
      window.localStorage.removeItem(LEGACY_PREFS_KEY);
      window.localStorage.removeItem(ANON_PREFS_KEY);
    } catch {
      /* ignore */
    }
  }, [v2Active, prefsSyncV2, invalidateGpsPlacement]);

  /**
   * What old callers read as `preferences`. In v2 the saved comune is projected
   * back to the v1 shape (registry labels/coordinates, read-only — plan l.70)
   * so LocationPill, LocalitaPopup and the galleries keep compiling and
   * rendering; the authoritative record is `preferencesV2`.
   */
  const exposedPreferences: UserPreferences = useMemo(() => {
    if (!v2Active || !prefsV2) return preferences;
    if (
      prefsV2.location.type === 'comune' &&
      projected &&
      projected.id === prefsV2.location.municipalityId
    ) {
      return {
        location: {
          type: 'cities',
          cities: [projected.city],
          radiusKm: prefsV2.radiusKm,
        },
        favoriteCategories: prefsV2.favoriteCategories,
      };
    }
    // Comune with the projection still loading (or unknown id), none,
    // needs_confirmation: no geography — the feed is held by the
    // migration/entry gate meanwhile. The radius is RETAINED in
    // `preferencesV2`, but the v1 shape has no place for it and it must not
    // act as geography.
    return { location: { type: 'none' }, favoriteCategories: prefsV2.favoriteCategories };
  }, [v2Active, prefsV2, projected, preferences]);

  const effectiveLocation = locationOverride ?? exposedPreferences.location;

  const value = useMemo<UserContextType>(
    () => ({
      preferences: exposedPreferences,
      preferencesV2: v2Active ? prefsV2 : null,
      locationNeedsConfirmation: v2Active ? locationNeedsConfirmation : false,
      setLocationChoice,
      clearLocation,
      setMunicipality,
      setSavedRadiusKm,
      toggleCategory,
      setFavoriteCategories,
      resetPreferences,
      locationOverride,
      setLocationOverride,
      clearLocationOverride,
      effectiveLocation,
      locationResolutionPending,
      clearLocationResolutionPending,
      prefsSyncNotice: notice,
      dismissPrefsSyncNotice: dismissNotice,
    }),
    [
      exposedPreferences,
      v2Active,
      prefsV2,
      locationNeedsConfirmation,
      setLocationChoice,
      clearLocation,
      setMunicipality,
      setSavedRadiusKm,
      toggleCategory,
      setFavoriteCategories,
      resetPreferences,
      locationOverride,
      setLocationOverride,
      clearLocationOverride,
      effectiveLocation,
      locationResolutionPending,
      clearLocationResolutionPending,
      notice,
      dismissNotice,
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

/**
 * Hook to access user preferences context.
 * @throws Error if used outside of UserProvider
 */
export function useUserContext(): UserContextType {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
}
